/**
 * Informe de conducción para el cliente, como HTML autocontenido.
 *
 * Es una función **pura**: mismos datos, mismo HTML, byte a byte. Vive en
 * `core/` justamente por eso — se puede generar en el navegador para que el
 * asesor lo descargue, o en el Worker para adjuntarlo a un correo, y sale
 * idéntico. Sin reloj, sin red, sin DOM.
 *
 * El archivo se abre en cualquier navegador y trae su propio botón para
 * guardarlo como PDF, así que no hace falta un motor de PDF en el servidor.
 *
 * Lo que este informe NO hace, y es deliberado:
 *
 *  - No presenta el puntaje como una verdad. Trae el DriveScore oficial de
 *    GuardIA (`drivescore-v1.ts`), con su tasa lineal por frecuencia y sus
 *    ponderaciones a la vista, y el informe imprime de dónde sale el número.
 *    Un puntaje que nadie puede reconstruir no es un puntaje: es una opinión
 *    con dos decimales.
 *  - No lo calcula sobre componentes que la cadencia dejó ciegos, ni sin
 *    distancia suficiente. Prefiere no publicarlo a publicarlo mal.
 *  - No llama «infracción» a nada. El umbral de velocidad es operacional.
 *  - No presenta un cero como buena conducción cuando la cadencia de muestreo
 *    dejó ciegos a los detectores: lo dice arriba, antes de las cifras.
 */

import type { DrivingAnalysisV5 } from "../pipeline/driving-analysis-v5.ts";
import type { SamplingQuality } from "../pipeline/sampling-quality.ts";
import {
  COMPONENT_ORDER,
  countEpisodesByFamily,
  EPISODE_TYPES_OF,
  explainRegimeZeros,
  explainUnmeasured,
  measurabilityFromReachability,
  UNMEASURED_NOTICE_TITLE,
  type FamilyMeasurability,
} from "./component-measurability.ts";
import {
  ACCIDENT_PROBABILITY_POLICY,
  computeDriveScore,
  COMPONENT_LABELS,
  publishesAccidentProbability,
  type AccidentProbabilityPolicy,
  type ScoreComponent,
} from "./drivescore-v1.ts";
import { zonedDate, zonedDateTime } from "./zoned-time.ts";
import {
  ACCIDENT_PROBABILITY_LABEL,
  EN_ACTUALIZACION,
  THEFT_EXPOSURE_LABEL,
  THEFT_EXPOSURE_POLICY,
} from "./sections-in-update.ts";
import { SCORE_GAUGE_LABELS } from "./guardia-score.ts";
/**
 * El informe solo imprime la procedencia, no la interpreta, así que acepta las
 * versiones como texto. Exigir los tipos literales del motor ataría el informe
 * a una versión concreta sin ganar nada.
 */
export type ReportProvenance = {
  analysisSignature: string;
  inputDigest: string;
  profileDigest: string;
  engineVersion: string;
  pipelineVersion: string;
};

/**
 * Versión de la plantilla del informe. Va impresa en el pie de cada HTML.
 *
 * - `v1`: hasta `c410ae9` (desplegado el 08/10/2026). Horas de los episodios en
 *   UTC bajo un encabezado en America/Bogota; probabilidad de accidente con las
 *   bandas del manual; hurto «No concluyente».
 * - `v2`: horas y fechas en la zona del informe; «Probabilidad de accidente» y
 *   «Exposición a hurto» en «En actualización» (ADR-014). Un HTML que diga `v1`
 *   en el pie es anterior a estas correcciones.
 * - `v3`: componentes por frecuencia lineal (ADR-016), con N, E y S visibles.
 */
export const CLIENT_REPORT_VERSION = "guardia-client-report-v3" as const;

export type ClientReportInput = {
  analysis: DrivingAnalysisV5;
  sampling: SamplingQuality;
  provenance: ReportProvenance;
  vehicle: { plate: string | null; name: string | null; group: string | null };
  /**
   * Si cada familia se pudo medir, tal como lo decidió el servidor. Este informe
   * NO lo vuelve a decidir: lo decidía antes, con su propia copia de la regla, y
   * podía discrepar de la pantalla sobre el mismo período.
   *
   * Ausente solo cuando la respuesta viene de una versión anterior del contrato.
   * Entonces se cae al criterio de antes —alcance físico más episodios— y queda
   * dicho en un solo sitio (`measurabilityFromReachability`).
   */
  measurability?: FamilyMeasurability[] | undefined;
  /** Instante de emisión, inyectado: el core no tiene reloj. */
  generatedAtIso: string;
  /**
   * Qué hacer con la probabilidad de accidente. Ausente, la política vigente
   * de `drivescore-v1.ts`. Existe para probar la opción `RETIRADA` del ADR-008
   * sin activarla.
   */
  accidentProbabilityPolicy?: AccidentProbabilityPolicy | undefined;
};

/** Escapa lo que vaya a texto. Ningún dato del proveedor entra crudo al HTML. */
function escape(value: string): string {
  return value
    .replace(/&/gu, "&amp;")
    .replace(/</gu, "&lt;")
    .replace(/>/gu, "&gt;")
    .replace(/"/gu, "&quot;");
}

/** Formato colombiano: coma decimal y punto de miles. */
function number(value: number | null, decimals = 2): string {
  if (value === null || !Number.isFinite(value)) return "—";
  const [whole, fraction] = value.toFixed(decimals).split(".");
  const grouped = whole!.replace(/\B(?=(\d{3})+(?!\d))/gu, ".");
  return fraction && Number(fraction) !== 0 ? `${grouped},${fraction}` : grouped;
}

function hoursAndMinutes(hours: number): string {
  const total = Math.round(hours * 60);
  return `${Math.floor(total / 60)} h ${String(total % 60).padStart(2, "0")} min`;
}

/**
 * Las cuatro familias, en el orden en que se le presentan al cliente.
 *
 * ## Por qué aquí no hay etiquetas
 *
 * Las había: «Aceleraciones», «Frenadas», «Giros», «Velocidad elevada». Y el
 * aviso de arriba de este mismo documento —que redacta `explainUnmeasured` con
 * `COMPONENT_LABELS`— las llamaba «Aceleraciones bruscas», «Frenadas bruscas»,
 * «Giros de riesgo» y «Velocidad alta operativa». El cliente leía el aviso
 * diciendo que «Frenadas bruscas» no se pudo medir y buscaba esa tarjeta entre
 * cuatro que no la mencionaban.
 *
 * Gana `COMPONENT_LABELS` (`drivescore-v1.ts`), y no por gusto:
 *
 *  - es el nombre del **manual** GuardIA, el mismo con que el puntaje reparte
 *    35 / 25 / 20 / 20, así que es el único de los dos que se puede rastrear a
 *    un documento de Grupo Arcos;
 *  - ya lo imprimen la tabla de componentes de la pantalla, el aviso de este
 *    informe y los motivos de exclusión del ranking de flota. Era el juego de
 *    nombres de un solo consumidor —estas tarjetas— contra el de todos los demás;
 *  - dice más: «Velocidad alta operativa» recuerda que el umbral es operacional
 *    y no un límite legal de vía, que es precisamente lo que este informe se
 *    cuida de no afirmar.
 *
 * `types` tampoco se escribe aquí: es `EPISODE_TYPES_OF`, que existe para que
 * ese mapa viva una sola vez (un tipo de episodio nuevo que se olvide en una
 * copia es una familia que se declara medida en un sitio y muda en el otro).
 */
const FAMILIES = [
  {
    key: "acceleration",
    component: "aceleraciones",
    color: "#1bc47d",
    meaning: "Aumentos de velocidad más fuertes de lo recomendable.",
    tip: "Acelerar de forma progresiva mejora el control y el consumo.",
  },
  {
    key: "braking",
    component: "frenadas",
    color: "#ff7d66",
    meaning: "Desaceleraciones marcadas, normalmente por falta de anticipación.",
    tip: "Mantener más distancia y anticipar el tráfico reduce estas situaciones.",
  },
  {
    key: "turn",
    component: "giros",
    color: "#00AEEF",
    meaning: "Cambios de dirección hechos con mayor exigencia.",
    tip: "Bajar la velocidad antes de la curva y girar progresivamente.",
  },
  {
    key: "elevated_speed",
    component: "velocidadAlta",
    color: "#f7b84b",
    meaning: "Tramos sostenidos por encima del umbral operacional del perfil.",
    tip: "Adaptar la velocidad al entorno y a la señalización.",
  },
] as const satisfies ReadonlyArray<{
  key: string;
  /** El componente del puntaje, que es de donde sale la etiqueta. */
  component: ScoreComponent;
  color: string;
  meaning: string;
  tip: string;
}>;

/** Los tipos de episodio de una familia, leídos del único mapa que los tiene. */
const typesOf = (family: (typeof FAMILIES)[number]): readonly string[] =>
  EPISODE_TYPES_OF[family.component];

const SEVERITY_LABEL: Record<string, string> = {
  low: "Baja",
  moderate: "Moderada",
  high: "Alta",
  critical: "Crítica",
  inconclusive: "Inconclusa",
};

export function buildClientReportHtml(input: ClientReportInput): string {
  const { analysis, sampling, provenance, vehicle } = input;

  /*
   * La medibilidad llega decidida. La ausencia es explícita, no un `?.` suelto:
   * una respuesta de una versión anterior del contrato no trae el bloque, y
   * entonces se usa el criterio de antes escrito una sola vez. Lo que NO vuelve
   * a pasar es que este informe la decida por su cuenta: eso era una segunda
   * copia de la regla, y el par de copias ya publicó un 73,8 «Bueno» sobre dos
   * detectores que nunca tuvieron oportunidad de dispararse.
   */
  const measurability =
    input.measurability ??
    measurabilityFromReachability({
      detectors: sampling.detectors,
      episodes: countEpisodesByFamily(analysis.technicalDriving.episodes),
    });
  const byFamily = FAMILIES.map((family) => {
    const episodes = analysis.technicalDriving.episodes.filter((episode) =>
      typesOf(family).includes(episode.type),
    );
    const detectorState = sampling.detectors.find((item) => item.detector === family.key);
    const measurable = measurability.find((item) => item.component === family.component);
    return {
      ...family,
      // El nombre del manual, el mismo que usa el aviso de arriba y la pantalla.
      label: COMPONENT_LABELS[family.component],
      total: episodes.length,
      relevant: episodes.filter((episode) =>
        ["moderate", "high", "critical"].includes(episode.severity),
      ).length,
      blind: measurable ? !measurable.measured : false,
      /** Por qué quedó muda, cuando lo está. Cadencia y umbral no son lo mismo. */
      reason: measurable?.reason ?? null,
      /** La cadencia pasa del techo de maniobras: lo detectado no es medición. */
      beyondCeiling: measurable?.beyondCadenceCeiling ?? false,
      requirement: detectorState?.requirement ?? null,
    };
  });

  const measured = byFamily.filter((family) => !family.blind);
  const measuredTotal = measured.reduce((sum, family) => sum + family.total, 0);

  /*
   * El puntaje por frecuencia de ADR-016 y promedio ponderado 35/25/20/20; un
   * componente que no se pudo medir sale del promedio
   * en vez de valer 100.
   *
   * Los componentes entran en `COMPONENT_ORDER`, NO en el orden de las tarjetas.
   * `distributeToHundred` reparte el sobrante del redondeo entre los que más
   * decimal perdieron y desempata **por índice**, así que el mismo período salía
   * con «Velocidad alta operativa 43,7 %» en el informe descargable y «43,8 %» en
   * la pantalla, que pasa los componentes en `COMPONENT_ORDER` porque así los
   * ordena el bloque de medibilidad del servidor. Un peso que cambia según por
   * qué puerta se mire el mismo vehículo no se puede defender ante quien pregunte
   * de dónde sale el número — y este informe existe para poder contestar eso.
   *
   * Se cierra en los dos consumidores y no en `computeDriveScore` porque la
   * función no tiene por qué conocer un orden canónico; el orden sí es una
   * decisión del informe. Queda anotado en el informe de este trabajo que el
   * desempate por índice sigue siendo una trampa para un tercer consumidor.
   */
  /*
   * La política se decide aquí y no se lee de `score.reading`: sin puntaje
   * —menos de 20 km, o ninguna familia medida— `reading` es `null`, y el pie
   * del informe seguía atribuyendo la probabilidad al manual con la política
   * `RETIRADA`.
   */
  const probabilityPolicy = input.accidentProbabilityPolicy ?? ACCIDENT_PROBABILITY_POLICY;
  const probabilityWithdrawn = !publishesAccidentProbability(probabilityPolicy);
  const probabilityInUpdate = probabilityPolicy === "EN_ACTUALIZACION";
  // Los dos apartados en actualización (ADR-014): solo el rótulo y el texto
  // exacto, sin porcentaje, rango, color ni subtítulo que sugiera un resultado.
  const inUpdateKpi = (label: string) =>
    `<div class="kpi"><div class="label">${escape(label)}</div><div class="value">${EN_ACTUALIZACION}</div></div>`;
  const probabilityKpi = probabilityInUpdate ? inUpdateKpi(ACCIDENT_PROBABILITY_LABEL) : "";
  const theftKpi = THEFT_EXPOSURE_POLICY === "EN_ACTUALIZACION" ? inUpdateKpi(THEFT_EXPOSURE_LABEL) : "";

  const score = computeDriveScore({
    distanceKm: analysis.exposure.eligibleDistanceKm,
    components: COMPONENT_ORDER.flatMap((component) => {
      const family = byFamily.find((item) => item.component === component);
      return family ? [{ component, episodes: family.total, measured: !family.blind }] : [];
    }),
    accidentProbabilityPolicy: probabilityPolicy,
  });

  const componentOf = (family: (typeof FAMILIES)[number]) =>
    score.components.find((item) => item.component === family.component);

  const title = escape(vehicle.plate ?? vehicle.name ?? "Vehículo");
  // Los límites se leen en la zona del informe, no cortando la cadena: un límite
  // que llegue en UTC caería en otro día.
  const timezone = analysis.period.timezone;
  const period = `${zonedDate(analysis.period.requestedFrom, timezone)} al ${zonedDate(analysis.period.requestedTo, timezone)}`;

  // El equipo sigue reportando con el vehículo parqueado, y esos pares de
  // puntos cuentan como tiempo elegible. Un promedio bajísimo lo delata, y
  // callarlo dejaría al cliente leyendo «manejó 377 horas para 307 km».
  const averageKmh =
    analysis.exposure.eligibleDurationHours > 0
      ? analysis.exposure.eligibleDistanceKm / analysis.exposure.eligibleDurationHours
      : null;
  const stoppedTimeWarning =
    averageKmh !== null && averageKmh < 5
      ? `<div class="alert">
           <strong>El tiempo cubierto incluye horas detenido</strong>
           <p>El promedio de ${number(averageKmh, 1)} km/h indica que buena parte de ese tiempo el
           vehículo estuvo parqueado con el equipo reportando. Las tasas por 100 km sí son
           comparables entre vehículos; las tasas por hora quedan diluidas por el tiempo detenido.</p>
         </div>`
      : "";

  /*
   * El aviso de lo que no se pudo medir, ARRIBA de las cifras (ADR-007).
   *
   * Antes lo disparaba `detectorsBlindedBySampling`, que solo habla de la
   * cadencia, y por eso el caso de DEVICE-REF-0016 no mostraba ninguno: la cadencia daba
   * de sobra y el umbral estaba por encima de todo lo que el vehículo hizo. Lo
   * dispara la medibilidad, y las líneas las redacta `explainUnmeasured`, que es
   * la única que sabe distinguir los dos motivos. Se decidió no repetirlas en el
   * aviso del puntaje —abajo— para que el porqué de cada familia esté escrito en
   * un solo lugar del documento.
   */
  const unmeasuredLines = explainUnmeasured(measurability, COMPONENT_LABELS);
  // Ceros medidos por la referencia de la cadencia (ADR-009): van junto al
  // puntaje, que es donde un 100 con cero episodios necesita su explicación.
  const regimeZeroLines = explainRegimeZeros(measurability, COMPONENT_LABELS);
  const blindWarning = unmeasuredLines.length
    ? `<div class="alert">
         <strong>${UNMEASURED_NOTICE_TITLE}</strong>
         ${sampling.explanation ? `<p>${escape(sampling.explanation)}</p>` : ""}
         <ul>${unmeasuredLines.map((line) => `<li>${escape(line)}</li>`).join("")}</ul>
       </div>`
    : "";

  const familyCards = byFamily
    .map((family) => {
      const component = componentOf(family);
      return `
    <div class="detail-card">
      <div class="detail-head">
        <span class="sw" style="background:${family.color}"></span>
        <div>
          <div class="detail-title">${family.label}</div>
          <div class="detail-sub">${
            family.blind
              ? "No se pudo medir en este período"
              : `${family.relevant} de ${family.total} con severidad moderada o mayor`
          }</div>
        </div>
        <span class="tag ${family.blind ? "na" : family.relevant > 0 ? "warn" : "good"}">${
          family.blind ? "Sin medición" : family.relevant > 0 ? "Requiere atención" : "Sin hallazgos"
        }</span>
      </div>
      <div class="metrics">
        <div><span class="num">${family.blind ? "—" : family.total}</span><span class="cap">veces detectado</span></div>
        <div><span class="num">${
          family.blind ? "—" : number(component?.ratePer100Km ?? null, 1)
        }</span><span class="cap">episodios / 100 km</span></div>
        <div><span class="num">${
          family.blind ? "—" : number(component?.score ?? null, 1)
        }</span><span class="cap">puntos del componente</span></div>
      </div>
      <p class="text"><strong>Qué significa:</strong> ${
        family.blind
          ? family.beyondCeiling
            ? "No se calificó porque el equipo reporta tan espaciado que una maniobra entera cabe entre dos lecturas: lo que se detecte en esta familia no es una medición. "
            : family.reason === "above_observed_range"
            ? // Decir «la frecuencia de reporte no lo permite» sería falso aquí:
              // la cadencia alcanzaba de sobra y el que no alcanzó fue el umbral.
              "No se calificó porque el umbral del perfil quedó por encima de todo lo que este vehículo hizo en el período, así que el detector no tuvo ninguna oportunidad de dispararse. "
            : `No se calificó porque la frecuencia de reporte del equipo no permite detectarlo. Habría hecho falta ${escape(family.requirement ?? "")}. `
          : family.meaning
      }</p>
      <p class="tip"><strong>Qué puedes mejorar:</strong> ${family.tip}</p>
    </div>`;
    })
    .join("");

  const episodeRows = analysis.technicalDriving.episodes
    .filter((episode) => ["moderate", "high", "critical"].includes(episode.severity))
    .slice(0, 20)
    .map((episode) => {
      const family = FAMILIES.find((item) => typesOf(item).includes(episode.type));
      return `<tr><td>${family ? COMPONENT_LABELS[family.component] : episode.type}</td><td>${SEVERITY_LABEL[episode.severity] ?? episode.severity}</td><td>${
        // El motor publica `start` en UTC; el informe declara su zona en el
        // encabezado y la hora tiene que estar en esa zona, con su fecha.
        episode.start ? escape(zonedDateTime(episode.start, timezone)) : "—"
      }</td></tr>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>Informe de conducción · ${title}</title>
<style>
:root{--line:#294965;--text:#edf6ff;--muted:#a8bfd5;--bg:#0a1d2d}
*{box-sizing:border-box}
body{margin:0;font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;background:linear-gradient(180deg,#06111b,var(--bg));color:var(--text)}
.container{max-width:860px;margin:0 auto;padding:16px}
.hero,.section{background:rgba(15,33,51,.96);border:1px solid var(--line);border-radius:22px;padding:20px}
.section{margin-top:14px}
.badge{display:inline-block;padding:6px 10px;font-size:11px;border-radius:999px;border:1px solid rgba(0,174,239,.28);color:#8fe5ff;text-transform:uppercase;letter-spacing:.08em}
h1{margin:12px 0 8px;font-size:28px;line-height:1.1}
h2{margin:0 0 6px;font-size:20px}
.sub{color:var(--muted);font-size:14px;margin:0}
.identity{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:16px}
.identity div{background:rgba(8,19,31,.35);border:1px solid var(--line);border-radius:14px;padding:10px}
.identity small{display:block;color:var(--muted);margin-bottom:3px}
.alert{margin-top:16px;background:rgba(247,184,75,.10);border:1px solid rgba(247,184,75,.32);border-radius:16px;padding:14px;color:#ffe1ac;font-size:14px;line-height:1.55}
.alert p{margin:6px 0 0}.alert ul{margin:8px 0 0 18px}.alert li{margin:4px 0}
.kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-top:16px}
.gauge{margin-top:14px}
.gauge-axis{display:flex;justify-content:space-between;font-size:11px;color:var(--muted);letter-spacing:.02em}
.gauge-track{position:relative;height:10px;margin-top:6px;border-radius:999px;background:linear-gradient(90deg,#ff7d66 0%,#f7b84b 45%,#1bc47d 100%);opacity:.35}
.gauge-fill{display:none}
.gauge-pin{position:absolute;top:-4px;width:3px;height:18px;border-radius:2px;background:var(--text);transform:translateX(-50%)}
.gauge-value{margin-top:12px;font-size:44px;font-weight:700;line-height:1;letter-spacing:-.02em}
.gauge-band{margin-top:2px;font-size:15px;font-weight:600;color:#f7b84b}
.bars{margin-top:12px;display:grid;gap:8px}
.bar-row{display:grid;grid-template-columns:170px 1fr 44px 76px;align-items:center;gap:10px;font-size:13px}
.bar-label{display:flex;align-items:center;gap:6px;color:var(--text)}
.bar-track{height:8px;border-radius:999px;background:rgba(255,255,255,.08);overflow:hidden}
.bar-fill{display:block;height:100%;border-radius:999px}
.bar-count{text-align:right;font-variant-numeric:tabular-nums;color:var(--muted)}
.bar-share{text-align:right;font-variant-numeric:tabular-nums;color:var(--muted)}
@media (max-width:620px){.bar-row{grid-template-columns:120px 1fr 36px 60px}}
.kpi{background:rgba(8,19,31,.48);border:1px solid var(--line);border-radius:18px;padding:14px}
.kpi .label{color:var(--muted);font-size:13px}
.kpi .value{font-size:28px;font-weight:800;margin-top:6px}
.kpi .subvalue{color:var(--muted);font-size:12px;margin-top:4px}
.detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:14px}
.detail-card{background:rgba(8,19,31,.38);border:1px solid var(--line);border-radius:18px;padding:14px}
.detail-head{display:flex;gap:10px;align-items:flex-start}
.detail-head>div{flex:1}
.detail-title{font-size:16px;font-weight:800}
.detail-sub{color:var(--muted);font-size:13px;margin-top:2px}
.metrics{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:12px}
.num{display:block;font-size:22px;font-weight:800}
.cap{color:var(--muted);font-size:12px}
.text,.tip{margin:10px 0 0;font-size:13px;line-height:1.5;color:#d9e9f8}
.sw{width:14px;height:14px;border-radius:5px;flex:0 0 auto;margin-top:4px}
.tag{display:inline-block;padding:5px 10px;border-radius:999px;font-size:11px;font-weight:800;white-space:nowrap}
.good{background:rgba(27,196,125,.12);color:#93f1c5;border:1px solid rgba(27,196,125,.24)}
.warn{background:rgba(255,142,90,.12);color:#ffc09d;border:1px solid rgba(255,142,90,.24)}
.na{background:rgba(168,191,213,.12);color:#cbd9e6;border:1px solid rgba(168,191,213,.22)}
table{width:100%;border-collapse:collapse;margin-top:12px;font-size:14px}
th{text-align:left;color:var(--muted);font-weight:600;font-size:12px;padding-bottom:6px}
td{padding:7px 0;border-top:1px solid rgba(148,163,184,.14)}
ul.rec{margin:10px 0 0;padding-left:20px}ul.rec li{margin:8px 0;font-size:14px;line-height:1.5}
.note{font-size:12px;color:var(--muted);line-height:1.55}
.sig{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;word-break:break-all;color:#8fb4d4}
.footer{text-align:center;color:var(--muted);font-size:12px;padding:18px 0 8px}
.printbar{display:flex;justify-content:flex-end;margin-bottom:12px}
.printbar button{border:0;background:#00AEEF;color:#03111c;border-radius:10px;padding:10px 16px;font-weight:800;cursor:pointer;font-size:14px}
@media(max-width:760px){.detail-grid,.kpi-grid,.identity{grid-template-columns:1fr 1fr}}
@media(max-width:480px){.detail-grid,.kpi-grid,.identity{grid-template-columns:1fr}}
@media print{
 body{background:#fff;color:#111}
 .hero,.section,.kpi,.detail-card,.identity div{background:#fff!important;color:#111!important;border-color:#bbb}
 .sub,.cap,.kpi .label,.kpi .subvalue,.detail-sub,.note,.footer,.sig{color:#555!important}
 .text,.tip{color:#111!important}
 .alert{background:#fff8e6!important;color:#5c4300!important}
 .printbar{display:none}
 .section{break-inside:avoid}
}
</style></head><body><div class="container">

<div class="printbar"><button onclick="window.print()">Guardar o imprimir en PDF</button></div>

<section class="hero">
  <span class="badge">Informe de conducción · GuardIA</span>
  <h1>Tu bienestar al volante nos importa<br>Vehículo ${title}</h1>
  <p class="sub">Período analizado: <strong>${escape(period)}</strong> · zona horaria ${escape(analysis.period.timezone)}</p>
  <div class="identity">
    <div><small>Cliente</small><strong>${escape(vehicle.group ?? "No suministrado")}</strong></div>
    <div><small>Estado del análisis</small><strong>${
      { VALID: "Completo", PARTIAL: "Parcial", INSUFFICIENT_DATA: "Datos insuficientes" }[analysis.status]
    }</strong></div>
    <div><small>Fuente</small><strong>WOX · Pipeline ${escape(provenance.pipelineVersion)}</strong></div>
  </div>
  ${blindWarning}
  ${stoppedTimeWarning}
  <div class="kpi-grid">
    <div class="kpi"><div class="label">Distancia recorrida</div><div class="value">${number(analysis.exposure.eligibleDistanceKm)}</div><div class="subvalue">km válidos</div></div>
    <div class="kpi"><div class="label">Tiempo cubierto</div><div class="value">${hoursAndMinutes(analysis.exposure.eligibleDurationHours)}</div><div class="subvalue">${
      analysis.exposure.eligibleDurationHours > 0 &&
      analysis.exposure.eligibleDistanceKm / analysis.exposure.eligibleDurationHours < 5
        ? "incluye tiempo detenido"
        : "tiempo elegible"
    }</div></div>
    <div class="kpi"><div class="label">Velocidad máxima</div><div class="value">${number(analysis.technicalDriving.maximumValidProviderSpeedKmh, 0)}</div><div class="subvalue">km/h · no equivale a exceso</div></div>
    <div class="kpi"><div class="label">Velocidad promedio</div><div class="value">${number(averageKmh, 1)}</div><div class="subvalue">km/h estimados</div></div>
    <div class="kpi"><div class="label">Viajes</div><div class="value">${analysis.exposure.eligibleTrips}</div><div class="subvalue">de ${analysis.exposure.candidateTrips} detectados</div></div>
  </div>
</section>

<section class="section">
  <h2>Conducción general</h2>
  ${
    score.driveScore === null
      ? `<div class="alert"><strong>Este período no lleva puntaje</strong><p>${escape(score.explanation)}</p></div>
         <div class="kpi-grid">
           <div class="kpi"><div class="label">Distancia analizada</div><div class="value">${number(score.distanceKm, 1)}</div><div class="subvalue">km</div></div>
           <div class="kpi"><div class="label">Episodios contados</div><div class="value">${score.components.reduce((sum, item) => sum + item.episodes, 0)}</div><div class="subvalue">en el período</div></div>
           ${probabilityKpi}
           ${theftKpi}
         </div>`
      : `<div class="gauge">
           <div class="gauge-axis">${SCORE_GAUGE_LABELS.map((label) => `<span>${label}</span>`).join("")}</div>
           <div class="gauge-track"><div class="gauge-pin" style="left:${score.driveScore}%"></div></div>
           <div class="gauge-value">${number(score.driveScore, 1)}</div>
           <div class="gauge-band">${escape(score.reading?.clientState ?? "")}</div>
           <p class="sub">${escape(score.explanation)}</p>
           ${
             regimeZeroLines.length
               ? `<ul class="sub">${regimeZeroLines.map((line) => `<li>${escape(line)}</li>`).join("")}</ul>`
               : ""
           }
         </div>
         <div class="kpi-grid">
           <div class="kpi"><div class="label">Riesgo de conducción</div><div class="value">${escape(score.reading?.risk ?? "—")}</div><div class="subvalue">DriveScore ${escape(score.version)}</div></div>
           ${
             probabilityInUpdate
               ? probabilityKpi
               : probabilityWithdrawn
                 ? ""
                 : `<div class="kpi"><div class="label">Probabilidad estimada de accidente</div><div class="value">${escape(score.reading?.accidentProbability ?? "—")}</div><div class="subvalue">estimación del modelo, no una predicción</div></div>`
           }
           <div class="kpi"><div class="label">Distancia analizada</div><div class="value">${number(score.distanceKm, 1)}</div><div class="subvalue">km</div></div>
           ${theftKpi}
         </div>`
  }
  ${
    score.partial
      ? `<div class="alert"><strong>El puntaje se calculó sobre parte de los componentes</strong><p>${score.unmeasured.length} componente(s) no se pudieron medir en este período —el motivo de cada uno está en el aviso del encabezado: no siempre es la cadencia de reporte—. En vez de contarlos como perfectos, salieron del promedio y su peso se repartió entre los demás: darle nota máxima a lo que no se midió sería premiar la falta de datos.</p></div>`
      : ""
  }
</section>

<section class="section">
  <h2>¿Qué está influyendo en tu conducción?</h2>
  <p class="sub">Peso de cada comportamiento en el resultado del período.</p>
  <div class="bars">
    ${byFamily
      .map((family) => {
        const contribution = componentOf(family);
        const share = family.blind ? null : (contribution?.effectiveWeight ?? 0);
        return `<div class="bar-row">
          <span class="bar-label"><span class="sw" style="background:${family.color}"></span>${family.label}</span>
          <span class="bar-track"><span class="bar-fill" style="width:${share ?? 0}%;background:${family.color}"></span></span>
          <span class="bar-count">${family.blind ? "—" : family.relevant}</span>
          <span class="bar-share">${share === null ? "No evaluada" : `${number(share, 1)}%`}</span>
        </div>`;
      })
      .join("")}
  </div>
  <p class="sub">Se analizaron ${measuredTotal} episodios de las familias que sí se pudieron medir. El porcentaje es el peso efectivo de cada componente en el promedio global.</p>
</section>

<section class="section">
  <h2>Qué influyó en tu conducción</h2>
  <p class="sub">Comportamientos detectados en el período. Son señales técnicas, no hechos probados sobre el conductor.</p>
  <div class="detail-grid">${familyCards}</div>
</section>

${
  episodeRows
    ? `<section class="section">
        <h2>Episodios destacados</h2>
        <p class="sub">Los de severidad moderada o mayor, en orden de aparición.</p>
        <table><thead><tr><th>Tipo</th><th>Severidad</th><th>Fecha y hora (${escape(timezone)})</th></tr></thead><tbody>${episodeRows}</tbody></table>
      </section>`
    : ""
}

<section class="section">
  <h2>Calidad de los datos</h2>
  <p class="sub">Qué tan confiable es la base sobre la que se calculó todo lo demás.</p>
  <div class="kpi-grid">
    <div class="kpi"><div class="label">Registros válidos</div><div class="value">${number(analysis.dataQuality.validPercent, 1)}%</div><div class="subvalue">de ${analysis.dataQuality.normalized} puntos</div></div>
    <div class="kpi"><div class="label">Puntos recibidos</div><div class="value">${analysis.dataQuality.received}</div><div class="subvalue">antes de depurar</div></div>
    <div class="kpi"><div class="label">Instantes duplicados</div><div class="value">${sampling.duplicateInstants}</div><div class="subvalue">${number(sampling.duplicateInstantPercent, 1)}% de los intervalos</div></div>
    <div class="kpi"><div class="label">Un punto cada</div><div class="value">${number(sampling.medianIntervalSeconds, 0)}</div><div class="subvalue">segundos en marcha (mediana)</div></div>
  </div>
</section>

<section class="section">
  <h2>Recomendaciones</h2>
  <ul class="rec">
    ${byFamily
      .filter((family) => !family.blind && family.relevant > 0)
      .map((family) => `<li>${family.tip}</li>`)
      .join("") ||
      "<li>No se consolidaron episodios que requieran atención en lo que sí se pudo medir. Mantener una conducción progresiva sigue siendo la mejor práctica.</li>"}
    <li>Conservar hábitos de cierre, verificación y parqueo en lugares iluminados o vigilados.</li>
  </ul>
</section>

<section class="section">
  <h2>Cómo interpretar este informe</h2>
  <p class="note">
    Los episodios provienen del Pipeline ${escape(provenance.pipelineVersion)} con el perfil técnico
    <strong>${escape(analysis.profile.profileId)} v${analysis.profile.version} (${escape(analysis.profile.status)})</strong>,
    y son <strong>señales técnicas</strong>: no constituyen hechos probados sobre el conductor ni una predicción de accidentes.
    El umbral de ${analysis.profile.elevatedSpeed.operationalSpeedLimitKmh} km/h es <strong>operacional de laboratorio</strong>,
    no un límite legal de vía, y no implica infracción.
    El puntaje <strong>${escape(score.version)}</strong> conserva los pesos y la clasificación del manual
    ${escape(score.manual)}. Para velocidad alta, frenadas, aceleraciones y giros calcula
    <strong>E = 100 × episodios consolidados / km elegibles</strong> y
    <strong>S = clamp(100 − E, 0, 100)</strong>, y promedia con las ponderaciones oficiales
    35 / 25 / 20 / 20. Un componente que no se
    pudo medir sale del promedio y su peso se reparte entre los demás, en vez de contarse como
    perfecto. ${
      probabilityInUpdate
        ? `Los apartados «${ACCIDENT_PROBABILITY_LABEL}» y «${THEFT_EXPOSURE_LABEL}» están <strong>en actualización</strong>: este informe no publica porcentajes de accidente ni una clasificación de hurto. La lectura de riesgo, cuando hay puntaje, es la banda del puntaje de conducción y no se traduce a un porcentaje.`
        : probabilityWithdrawn
        ? "Este informe <strong>no publica una probabilidad de accidente</strong>: la lectura de riesgo, cuando hay puntaje, es la banda del puntaje y no se traduce a un porcentaje."
        : "La probabilidad de accidente es la lectura de referencia del manual asociada a la banda del puntaje: es una <strong>estimación del modelo, no una predicción</strong> sobre esta persona ni un puntaje de aseguradora."
    } Sirve para comparar períodos del mismo vehículo, o
    vehículos con la misma cadencia de reporte.
    ${
      analysis.profile.calibration === "UNCALIBRATED"
        ? "Los umbrales de este perfil son <strong>provisionales</strong>, así que el puntaje lo es también: los umbrales deciden cuántos episodios hay, y los episodios deciden el puntaje."
        : ""
    }
    Los registros erróneos o no concluyentes se separaron y no afectaron el resultado.
  </p>
  <p class="note" style="margin-top:14px">
    <strong>Firma del análisis</strong><br>
    <span class="sig">${escape(provenance.analysisSignature)}</span><br>
    Motor ${escape(provenance.engineVersion)} · informe ${CLIENT_REPORT_VERSION} · emitido ${escape(zonedDateTime(input.generatedAtIso, timezone))} (${escape(timezone)}) · ${escape(input.generatedAtIso)}<br>
    Dos informes con la misma firma son el mismo informe. Con los mismos datos de entrada, este vuelve a producirse idéntico.
  </p>
</section>

<div class="footer">GuardIA · Informe de conducción · Generado para concientización y retroalimentación</div>
</div></body></html>`;
}
